
import { NightlyPrice } from "./NightlyPrices";
import { Currency } from "../../currencies/models/Currency";
import { DataTableColumn } from "../../../shared/components/data-table/DataTableColumn.model";

export interface Policy {
    id: number;
    policy_id?: number;
    insurance_policy_id: number;
    organization_id: number | string;
    property_id: number;
    is_active: boolean;
    created_date: Date;
    policy_price_id: number;
    updated_date: Date;
    account_id: number;
    status_id: number;
    original_id: number;
    is_primary: number;
    policy: PolicyDetails;
    policy_price: PolicyPrice;
    status: PolicyStatus;
}

export interface PolicyDetails {
    id: number;
    name: string;
    range_guests_from: string;
    range_guests_to: string;
    damage_coverage: number;
    liability_coverage: number;
    dwelling_coverage: number;
    all_countries: boolean;
    range_reservation_price_from: string;
    range_reservation_price_to: string;
    range_no_of_bedrooms_from: string;
    range_no_of_bedrooms_to: string;
    currency_id: number;
    status_id: number;
    account_id: number;
    created_date: Date;
    description: string;
    note: string;
}

export interface PolicyPrice {
    id: number;
    original_id?: number;
    type_id: number;
    name: string;
    description: string;
    currency_id: number;
    currency: Currency;
    pricing_model?: any;
    pricing_model_id: number;
    deductible: number;
    price: number;
    extra_night_price: number;
    max_nights?: number | null;
    status_id: number;
    account_id?: number;
    is_active: boolean;
    night_ranges: NightlyPrice[];
    created_date?: Date;
    updated_date?: Date;
}

export interface PolicyStatus {
    id: number;
    code: string;
    display_text: string;
    display_order: number;
}

export interface PolicyUnderwriter {
    id: number;
    account_id: number;
    name: string;
    description: string;
    is_active: number;
    created_date: Date;
    updated_date: Date;
}

export interface PolicyUnderwriterResponse {
    id: number;
    name: string;
    description: string;
    account_id: number;
    is_active: number;
    original_id: number;
    policy_coverage_id: number;
    policy_id: number;
    policy_price: PolicyPrice;
    policy_price_id: number;
    status: PolicyStatus;
    status_id: number;
    underwriter: PolicyUnderwriter;
    underwriter_id: number;
    created_date: Date;
    updated_date: Date;
}

export interface PolicyRow {
    id: number;
    accountId?: number;
    insurancePolicyId?: number;
    policyPriceId?: number;
    name: string;
    contentsCoverage: string | number;
    hoLiabilityCoverage: string | number;
    dwellingCoverage: string | number;
    priceRate?: string;
    priceModel?: string;
    deductible?: string;
    price?: number | string;
    pillStatus: string;
    statusId?: number;
}

export interface UpdateReservationPolicyRow {
    id: number;
    insurancePolicyId: number;
    policyPriceId: number;
    name: string;
    contentsCoverage: string;
    hoLiabilityCoverage: string;
    dwellingCoverage: string;
    priceRate: string;
    priceModel: string;
    deductible: string;
    price: number | string;
    extraNightPrice: number | string;
}

export interface PolicyUnderwriterRow {
    id: number;
    name: string;
    policy_price_name: string;
    policyPriceId?: number,
    pricing_model: string;
    price_rate_id: number;
    underwriter_id: number;
    price: string;
    deductible: string;
    max_nights?: number | null;
}

export const UpdateReservationPolicyColumns: DataTableColumn[] = [
    { column: 'insurancePolicyId', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contentsCoverage', display: 'Contents Coverage' },
    { column: 'hoLiabilityCoverage', display: 'Liability Coverage' },
    { column: 'dwellingCoverage', display: 'Dwelling Coverage' },
    { column: 'priceRate', display: 'Price Rate Name' },
    { column: 'priceModel', display: 'Pricing Model' },
    { column: 'deductible', display: 'Deductible' },
    { column: 'price', display: 'Price' },
    { column: 'extraNightPrice', display: 'Extra Night Price' },
    { column: 'action', display: 'Action' }
]

export const UpdateRemovePropertyPolicyColumns: DataTableColumn[] = [
    { column: 'insurancePolicyId', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contentsCoverage', display: 'Contents Coverage' },
    { column: 'hoLiabilityCoverage', display: 'Liability Coverage' },
    { column: 'dwellingCoverage', display: 'Dwelling Coverage' },
    { column: 'remove', display: 'Remove' }
]

export const OrganizationPolicyColumns: DataTableColumn[] = [
    { column: 'insurancePolicyId', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contentsCoverage', display: 'Contents Coverage' },
    { column: 'hoLiabilityCoverage', display: 'Liability Coverage' },
    { column: 'dwellingCoverage', display: 'Dwelling Coverage' },
    { column: 'priceRate', display: 'Price Rate Name' },
    { column: 'priceModel', display: 'Pricing Model' },
    { column: 'deductible', display: 'Deductible' },
    { column: 'price', display: 'Price' },
    { column: 'pillStatus', display: 'Primary' },
    { column: 'organizationPolicyButtons', display: '' }
]

export const PropertyPolicyColumns: DataTableColumn[] = [
    { column: 'insurancePolicyId', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contentsCoverage', display: 'Contents Coverage' },
    { column: 'hoLiabilityCoverage', display: 'Liability Coverage' },
    { column: 'dwellingCoverage', display: 'Dwelling Coverage' },
    { column: 'priceRate', display: 'Price Rate Name' },
    { column: 'priceModel', display: 'Pricing Model' },
    { column: 'deductible', display: 'Deductible' },
    { column: 'price', display: 'Price' },
    { column: 'pillStatus', display: 'Primary' },
    { column: 'propertyPolicyButtons', display: '' }
]

export const ReservationInsurancePolicyColumns: DataTableColumn[] = [
    { column: 'name', display: 'Name' },
    { column: 'contents_coverage', display: 'Contents Coverage' },
    { column: 'ho_liability_coverage', display: 'Liability Coverage' },
    { column: 'dwelling_coverage', display: 'Dwelling Coverage' },
    { column: 'pricing_model', display: 'Pricing Model' },
    { column: 'stay_length', display: 'Stay Length' },
    { column: 'price', display: 'Price' },
    { column: 'extra_night_price', display: 'Extra Night Price' }
]

export const PolicyUnderwriterColumns: DataTableColumn[] = [
    { column: 'id', display: 'ID' },
    { column: 'name', display: 'Underwriter' },
    { column: 'policy_price_name', display: 'Price Rate Name' },
    { column: 'pricing_model', display: 'Pricing Model' },
    { column: 'price', display: 'Price' },
    { column: 'deductible', display: 'Deductible' },
    { column: 'policyUnderwriterButtons', display: '' }
]

export const PolicyPriceColumns: DataTableColumn[] = [
    { column: 'id', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'pricing_model', display: 'Pricing Model' },
    { column: 'currency', display: 'Currency' },
    { column: 'price', display: 'Price' },
    { column: 'deductible', display: 'Deductible' }
]